import { useMemo, useState } from 'react';

import {
  FAIconName,
  FireflyKebabMenu,
  FireflyKebabMenuItem,
  useToastMessage,
} from '@equip.health/ui';

import ConfirmModal from '~/components/common/ConfirmModal';
import { GlobalErrorCode } from '~/lib/constants/global.constants';
import { MaudPermissions } from '~/lib/constants/permissions.constants';
import { CANCEL_EVENT_MODAL } from '~/lib/constants/schedule.constants';
import { GroupClassEventOption } from '~/lib/constants/scheduling.constants';
import { useGlobalContext } from '~/lib/contexts/GlobalContext';
import useErrorToast from '~/lib/hooks/useErrorToast';
import { getGroupClassEventOptions } from '~/lib/util/schedule/schedule.util';
import {
  useCancelEventMutation,
  useDeclineEventMutation,
  useLazyGetGroupClassDetailQuery,
} from '~/store/services/groupClass';

import AddHostsModal from '../modals/AddHostsModal';
import GroupClassDetailsModal from '../modals/GroupClassDetailsModal';
import errorConstant from '~/lib/constants/error.constants';
import { useIsPatientPath } from '~/lib/hooks/useIsPatientPath';
const TOP_LEVEL_ID = 'group-class-event-kebab-menu';

interface GroupClassEventKebabMenuProps {
  groupClassEventExternalId: string;
  groupClassExternalId: string;
  hostIds: string[];
  isAttendee: boolean;
  isPastEvent: boolean;
  onRemoveEvent?: (eventId: string, isRemoveEvent?: boolean) => void;
}

const GroupClassEventKebabMenu = ({
  groupClassEventExternalId,
  groupClassExternalId,
  hostIds,
  isAttendee,
  isPastEvent,
  onRemoveEvent,
}: GroupClassEventKebabMenuProps) => {
  const [isOpenHostsModal, setIsOpenHostsModal] = useState<boolean>(false);
  const [isOpenDetailsModal, setIsOpenDetailsModal] = useState<boolean>(false);
  const [isCancelEventWarningModalOpen, setIsCancelEventWarningModalOpen] =
    useState<boolean>(false);

  const { createToast } = useToastMessage();
  const {
    currentUser: { userId: userExternalId },
    permissions,
  } = useGlobalContext();

  const isPatientPath = useIsPatientPath();

  const [getGroupClass, { data: groupClass }] =
    useLazyGetGroupClassDetailQuery();

  const [declineEvent] = useDeclineEventMutation();
  const [cancelEvent, { isLoading: isCancelEventLoading }] =
    useCancelEventMutation();

  const { showErrorToast } = useErrorToast();

  const onDeclineEvent = (): void => {
    declineEvent(groupClassEventExternalId)
      .unwrap()
      .then(() => {
        if (onRemoveEvent) {
          onRemoveEvent(groupClassEventExternalId, true);
        }
      })
      .catch(() => {
        createToast({
          icon: FAIconName.exclamationTriangleRegular,
          id: `${TOP_LEVEL_ID}`,
          text: errorConstant.DeclineEventError,
          variant: 'critical',
        });
      });
  };

  const onCancelEvent = (): void => {
    setIsCancelEventWarningModalOpen(true);
  };

  const handleConfirmCancelEvent = (): void => {
    cancelEvent(groupClassEventExternalId)
      .unwrap()
      .then(() => {
        setIsCancelEventWarningModalOpen(false);
        if (onRemoveEvent) {
          onRemoveEvent(groupClassEventExternalId, true);
        }
      })
      .catch((error) => {
        setIsCancelEventWarningModalOpen(false);
        showErrorToast(
          GlobalErrorCode.CANCEL_GROUP_CLASS_EVENT,
          error.status || 500,
          error.data || 'Unknown error',
        );
      });
  };

  const handleDiscardCancelEvent = (): void => {
    setIsCancelEventWarningModalOpen(false);
  };

  const onViewClassDetails = (): void => {
    getGroupClass({ groupClassExternalId });
    setIsOpenDetailsModal(true);
  };

  const kebabMenuOptionsList: Partial<
    Record<
      GroupClassEventOption,
      {
        label: string;
        leftIcon: FAIconName;
        leftIconColor: string;
        onClick: () => void;
      }
    >
  > = {
    [GroupClassEventOption.VIEW_CLASS_INFO]: {
      label: 'View group details',
      leftIcon: FAIconName.infoCircleRegular,
      leftIconColor: 'neutral.primary',
      onClick: onViewClassDetails,
    },
    [GroupClassEventOption.DECLINE]: {
      label: 'Decline this event',
      leftIcon: FAIconName.eyeSlashRegular,
      leftIconColor: 'neutral.primary',
      onClick: onDeclineEvent,
    },
    [GroupClassEventOption.ADD_HOST]: {
      label: 'Add host for this event',
      leftIcon: FAIconName.userPlusRegular,
      leftIconColor: 'neutral.primary',
      onClick: () => setIsOpenHostsModal(true),
    },
    [GroupClassEventOption.CANCEL]: {
      label: 'Cancel this event',
      leftIcon: FAIconName.XmarkRegular,
      leftIconColor: 'critical.base',
      onClick: onCancelEvent,
    },
  };

  const groupClassEventKebabMenuListV2: FireflyKebabMenuItem[] = useMemo(() => {
    const options = getGroupClassEventOptions(
      hostIds.includes(userExternalId),
      isAttendee,
      permissions[MaudPermissions.MAUD_SCHEDULE_PROXY],
      isPastEvent,
      isPatientPath,
      hostIds?.length === 1,
    );
    const shouldShowKebabOption = (option: {
      isDisabled: boolean;
      isHidden: boolean;
    }) => {
      return !option.isHidden && !option.isDisabled;
    };

    return Object.entries(kebabMenuOptionsList)
      .map(([option, value]) => shouldShowKebabOption(options[option]) && value)
      .filter((each) => each);
  }, [hostIds, isAttendee, permissions, userExternalId]);

  return (
    <>
      {groupClassEventKebabMenuListV2.length > 0 && (
        <FireflyKebabMenu
          aria-label="appointment options"
          options={groupClassEventKebabMenuListV2}
        />
      )}
      <ConfirmModal
        body={CANCEL_EVENT_MODAL.message}
        cancelLabel={CANCEL_EVENT_MODAL.cancelButtonLabel}
        confirmLabel={CANCEL_EVENT_MODAL.confirmButtonLabel}
        confirmVariant="accent.critical"
        id={`${TOP_LEVEL_ID}__cancel-event__warning`}
        isLoading={isCancelEventLoading}
        isOpen={isCancelEventWarningModalOpen}
        onClose={handleDiscardCancelEvent}
        onConfirm={handleConfirmCancelEvent}
        title={CANCEL_EVENT_MODAL.title}
      />
      <AddHostsModal
        groupClassEventExternalId={groupClassEventExternalId}
        hostIds={hostIds}
        isOpen={isOpenHostsModal}
        onClose={() => setIsOpenHostsModal(false)}
      />
      <GroupClassDetailsModal
        groupClass={groupClass?.data}
        isOpen={isOpenDetailsModal}
        isRegisterEnabled={false}
        onClose={() => {
          setIsOpenDetailsModal(false);
        }}
        onRegistration={() => {
          getGroupClass({ groupClassExternalId });
        }}
      />
    </>
  );
};

export default GroupClassEventKebabMenu;
